/**
 * Share Button block — popover toggle.
 *
 * Handles open/close of the AddToAny share popover. The popover is
 * visibility-hidden (not display:none) so AddToAny's JS can initialise
 * its share links on page load before the popover is first opened.
 */

/**
 * @param {HTMLElement} block
 */
function initBlock( block ) {
	const trigger = block.querySelector( '.block-share-button__trigger' );
	const popup   = block.querySelector( '.block-share-button__popup' );

	if ( ! trigger || ! popup ) {
		return;
	}

	const focusableSelector = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])',
	].join( ', ' );

	const getFocusableElements = () =>
		Array.from( popup.querySelectorAll( focusableSelector ) ).filter( ( element ) => {
			if (
				element.hasAttribute( 'disabled' ) ||
				element.getAttribute( 'aria-hidden' ) === 'true'
			) {
				return false;
			}

			const style = window.getComputedStyle( element );
			return style.display !== 'none' && style.visibility !== 'hidden';
		} );

	const setOpen = ( open ) => {
		block.classList.toggle( 'is-open', open );
		trigger.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		popup.setAttribute( 'aria-hidden', open ? 'false' : 'true' );

		if ( open ) {
			// Move focus into the popover so keyboard users can reach share links.
			window.requestAnimationFrame( () => {
				const focusables = getFocusableElements();
				( focusables[ 0 ] || popup ).focus();
			} );
		}
	};

	trigger.addEventListener( 'click', () => {
		setOpen( ! block.classList.contains( 'is-open' ) );
	} );

	// Close on outside click.
	document.addEventListener( 'click', ( e ) => {
		if ( ! block.classList.contains( 'is-open' ) ) {
			return;
		}
		if ( ! block.contains( e.target ) ) {
			setOpen( false );
		}
	} );

	// Close on Escape and keep keyboard focus inside the open modal popover.
	document.addEventListener( 'keydown', ( e ) => {
		if ( ! block.classList.contains( 'is-open' ) ) {
			return;
		}

		if ( e.key === 'Escape' ) {
			setOpen( false );
			trigger.focus();
			return;
		}

		if ( e.key !== 'Tab' ) {
			return;
		}

		const focusables = getFocusableElements();
		if ( focusables.length === 0 ) {
			e.preventDefault();
			popup.focus();
			return;
		}

		const first = focusables[ 0 ];
		const last = focusables[ focusables.length - 1 ];
		const current = document.activeElement;

		if ( e.shiftKey ) {
			if ( current === first || ! popup.contains( current ) ) {
				e.preventDefault();
				last.focus();
			}
		} else if ( current === last || ! popup.contains( current ) ) {
			e.preventDefault();
			first.focus();
		}
	} );
}

export function initShareButton() {
	document.querySelectorAll( '.block-share-button' ).forEach( initBlock );
}
